import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import elexerMascot from '../../Assets/Elexer_mascot_animated.gif';
import { aiChatService } from '../../_services/aiChat.service';
import AIChatModal from './AIChatModal';
import {
    clampLauncherPosition,
    getDefaultLauncherPosition,
    hasExceededDragThreshold,
} from './aiChatLauncherGeometry';
import './AIChat.css';

const MAX_HISTORY_MESSAGES = 30;

const getViewport = () => {
    if (typeof window === 'undefined') {
        return {
            width: 112,
            height: 112,
        };
    }

    return {
        width: window.innerWidth,
        height: window.innerHeight,
    };
};

export const TOOLTIP_SESSION_KEY = 'aiChat.tooltip.seen';
let messageSequence = 0;

export function getInitialTooltipVisibility(
    storage = typeof window !== 'undefined' ? window.sessionStorage : null
) {
    if (!storage) return true;

    try {
        return storage.getItem(TOOLTIP_SESSION_KEY) !== '1';
    } catch (error) {
        return true;
    }
}

export function markTooltipSeen(
    storage = typeof window !== 'undefined' ? window.sessionStorage : null
) {
    if (!storage) return;

    try {
        storage.setItem(TOOLTIP_SESSION_KEY, '1');
    } catch (error) {
        // The tooltip can still be dismissed when browser storage is unavailable.
    }
}

function nextMessageId(role) {
    messageSequence += 1;
    return `${Date.now()}-${role}-${messageSequence}`;
}

function historyKeyForUser(user) {
    return user && user.id ? `aiChat.history.${user.id}` : null;
}

function loadHistory(user) {
    const historyKey = historyKeyForUser(user);
    if (!historyKey) return [];

    try {
        const saved = JSON.parse(sessionStorage.getItem(historyKey) || '[]');
        return Array.isArray(saved) ? saved.slice(-MAX_HISTORY_MESSAGES) : [];
    } catch (error) {
        return [];
    }
}

export const AIChatTooltip = ({ onDismiss, onOpen }) => (
    <aside
        className="ai-chat-tooltip"
        aria-label="Dataco AI introduction"
    >
        <button
            type="button"
            className="ai-chat-tooltip-close"
            aria-label="Dismiss AI Chat introduction"
            onClick={onDismiss}
        >
            <span aria-hidden="true">×</span>
        </button>
        <button
            type="button"
            className="ai-chat-tooltip-content"
            aria-label="Open Dataco AI"
            onClick={onOpen}
        >
            <span className="ai-chat-tooltip-eyebrow">
                <span className="ai-chat-tooltip-status" aria-hidden="true" />
                Dataco AI
            </span>
            <span className="ai-chat-tooltip-headline">
                Your dashboard just learned how to answer back.
            </span>
            <span className="ai-chat-tooltip-copy">
                Ask about inventory, orders, quality, and more.
            </span>
            <span className="ai-chat-tooltip-action" aria-hidden="true">
                Start a conversation
                <span>↗</span>
            </span>
        </button>
    </aside>
);

const AIChatWidget = ({ user }) => {
    const [enabled, setEnabled] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isLauncherDismissed, setIsLauncherDismissed] = useState(false);
    const [launcherPosition, setLauncherPosition] = useState(() => getDefaultLauncherPosition(getViewport()));
    const [showTooltip, setShowTooltip] = useState(getInitialTooltipVisibility);
    const [messages, setMessages] = useState(() => loadHistory(user));
    const [pending, setPending] = useState(false);
    const controllerRef = useRef(null);
    const launcherDragRef = useRef(null);
    const suppressLauncherClickRef = useRef(false);
    const historyKey = useMemo(() => historyKeyForUser(user), [user && user.id]);

    useEffect(() => {
        let active = true;

        aiChatService.getStatus()
            .then(result => {
                if (active) setEnabled(Boolean(result && result.enabled));
            })
            .catch(() => {
                if (active) setEnabled(false);
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        setMessages(loadHistory(user));
    }, [historyKey]);

    useEffect(() => {
        if (!historyKey) return;
        try {
            sessionStorage.setItem(
                historyKey,
                JSON.stringify(messages.slice(-MAX_HISTORY_MESSAGES))
            );
        } catch (error) {
            // History is optional; keep the in-memory conversation when storage is full.
        }
    }, [historyKey, messages]);

    useEffect(() => () => {
        const controller = controllerRef.current;
        if (controller) controller.abort();
        launcherDragRef.current = null;
    }, []);

    useEffect(() => {
        const handleLauncherResize = () => {
            setLauncherPosition(current => clampLauncherPosition(current, getViewport()));
        };

        window.addEventListener('resize', handleLauncherResize);
        return () => window.removeEventListener('resize', handleLauncherResize);
    }, []);

    const sendQuestion = useCallback(async (rawQuestion) => {
        const question = String(rawQuestion || '').trim();
        if (!question || pending || !user || !user.id) return;

        const userMessage = {
            id: nextMessageId('user'),
            role: 'user',
            text: question,
        };
        setMessages(current => [...current, userMessage].slice(-MAX_HISTORY_MESSAGES));
        setPending(true);

        const controller = new AbortController();
        controllerRef.current = controller;

        try {
            const response = await aiChatService.askQuestion(user.id, question, controller.signal);
            setMessages(current => [
                ...current,
                {
                    id: nextMessageId('assistant'),
                    role: 'assistant',
                    data: response,
                    originalQuestion: question,
                },
            ].slice(-MAX_HISTORY_MESSAGES));
        } catch (error) {
            if (error && error.kind === 'aborted') return;
            setMessages(current => [
                ...current,
                {
                    id: nextMessageId('assistant-error'),
                    role: 'assistant',
                    transportError: error || { kind: 'network' },
                    originalQuestion: question,
                },
            ].slice(-MAX_HISTORY_MESSAGES));
        } finally {
            if (controllerRef.current === controller) {
                controllerRef.current = null;
            }
            setPending(false);
        }
    }, [pending, user && user.id]);

    const clearChat = useCallback(() => {
        setMessages([]);
        if (historyKey) {
            try {
                sessionStorage.removeItem(historyKey);
            } catch (error) {
                // Clearing in-memory history is sufficient when storage is unavailable.
            }
        }
    }, [historyKey]);

    const dismissTooltip = useCallback(() => {
        setShowTooltip(false);
        markTooltipSeen();
    }, []);

    const openChat = useCallback(() => {
        setShowTooltip(false);
        markTooltipSeen();
        setIsOpen(true);
    }, []);

    const handleLauncherPointerDown = event => {
        if (event.button !== 0) return;

        launcherDragRef.current = {
            startPoint: {
                x: event.clientX,
                y: event.clientY,
            },
            startPosition: launcherPosition,
            moved: false,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handleLauncherPointerMove = event => {
        const drag = launcherDragRef.current;
        if (!drag) return;

        const point = {
            x: event.clientX,
            y: event.clientY,
        };
        if (!drag.moved && !hasExceededDragThreshold(drag.startPoint, point)) return;

        drag.moved = true;
        setLauncherPosition(clampLauncherPosition({
            x: drag.startPosition.x + point.x - drag.startPoint.x,
            y: drag.startPosition.y + point.y - drag.startPoint.y,
        }, getViewport()));
    };

    const handleLauncherPointerEnd = event => {
        const drag = launcherDragRef.current;
        if (!drag) return;

        suppressLauncherClickRef.current = drag.moved;
        launcherDragRef.current = null;

        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    const handleLauncherClick = () => {
        if (suppressLauncherClickRef.current) {
            suppressLauncherClickRef.current = false;
            return;
        }

        openChat();
    };

    if (enabled !== true) return null;

    return (
        <>
            {!isOpen && !isLauncherDismissed && (
                <div
                    className="ai-chat-entry"
                    style={{
                        '--ai-chat-entry-x': `${launcherPosition.x}px`,
                        '--ai-chat-entry-y': `${launcherPosition.y}px`,
                    }}
                >
                    {showTooltip ? (
                        <AIChatTooltip
                            onDismiss={dismissTooltip}
                            onOpen={openChat}
                        />
                    ) : null}
                    <div className="ai-chat-launcher-wrap">
                        <button
                            type="button"
                            className="ai-chat-launcher"
                            aria-label="Open AI assistant"
                            onPointerDown={handleLauncherPointerDown}
                            onPointerMove={handleLauncherPointerMove}
                            onPointerUp={handleLauncherPointerEnd}
                            onPointerCancel={handleLauncherPointerEnd}
                            onClick={handleLauncherClick}
                        >
                            <img
                                className="ai-chat-mascot ai-chat-mascot--launcher"
                                src={elexerMascot}
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <button
                            type="button"
                            className="ai-chat-launcher-dismiss"
                            aria-label="Dismiss AI assistant"
                            title="Dismiss AI assistant"
                            onPointerDown={event => event.stopPropagation()}
                            onClick={() => setIsLauncherDismissed(true)}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                </div>
            )}
            <AIChatModal
                show={isOpen}
                messages={messages}
                pending={pending}
                onHide={() => setIsOpen(false)}
                onClear={clearChat}
                onSend={sendQuestion}
                onRetry={sendQuestion}
            />
        </>
    );
};

export default AIChatWidget;
